import type { QueryClient } from '@tanstack/react-query';
import { doc, getDoc, getDocs, setDoc } from 'firebase/firestore';
import type { Contact, ContactStage } from '@otw/models/leads/contacts';
import {
  appendContactStageHistory,
  getCurrentContactStage,
} from '@otw/models/leads/contactStageHistory';
import type { InventoryLead } from '@otw/models/leads/inventoryLead';
import type { Lead, LeadStatus } from '@otw/models/leads/lead';
import { db } from '@otw/models/db';

import { normalizeText } from './customerUtils';
import {
  getCustomerKeyForLead,
  type TaskCustomerLeadSummary,
  type TaskCustomerOption,
} from './taskUtils';

const CONTACTS_QUERY_KEY = ['contacts'] as const;
const INVENTORY_LEADS_QUERY_KEY = ['inventory_leads'] as const;

export function getContactCustomerKey(contact: Contact): string {
  return (
    normalizeText(contact.email) ||
    normalizeText(contact.phone) ||
    normalizeText(contact.name) ||
    contact.id
  );
}

/** True when lead is linked by contactId, leadIds, or legacy email/phone/name key. */
export function contactMatchesLead(contact: Contact, lead: Lead): boolean {
  const leadContactId = lead.contactId?.trim();
  if (leadContactId && leadContactId === contact.id) return true;
  if (contact.leadIds?.includes(lead.id)) return true;
  return getContactCustomerKey(contact) === getCustomerKeyForLead(lead);
}

const summarizeLeadForTask = (lead: Lead): TaskCustomerLeadSummary => ({
  id: lead.id,
  company: lead.company?.trim() ? lead.company.trim() : null,
  createdAt: lead.createdAt instanceof Date ? lead.createdAt : null,
});

const sortLeadSummariesForTask = (
  summaries: TaskCustomerLeadSummary[],
): TaskCustomerLeadSummary[] =>
  [...summaries].sort((a, b) => {
    const aTime = a.createdAt ? a.createdAt.getTime() : 0;
    const bTime = b.createdAt ? b.createdAt.getTime() : 0;
    return bTime - aTime;
  });

const collectLeadsForContact = (contact: Contact, leads: Lead[]): Lead[] => {
  const matched: Lead[] = [];
  const seen = new Set<string>();
  for (const lead of leads) {
    if (!contactMatchesLead(contact, lead)) continue;
    if (seen.has(lead.id)) continue;
    seen.add(lead.id);
    matched.push(lead);
  }
  return matched;
};

/** Task form customer row for a single CRM contact (id = Firestore contact doc id). */
export function contactToTaskCustomerOption(
  contact: Contact,
  leads: Lead[],
): TaskCustomerOption {
  const contactLeads = collectLeadsForContact(contact, leads);
  return {
    id: contact.id,
    displayName: contact.name || 'Unnamed contact',
    email: contact.email ?? null,
    phone: contact.phone ?? null,
    leads: sortLeadSummariesForTask(contactLeads.map(summarizeLeadForTask)),
  };
}

/** All CRM contacts as task customers; optional linked leads populate the lead picker. */
export function buildTaskCustomerOptionsFromContacts(
  contacts: Contact[],
  leads: Lead[],
): TaskCustomerOption[] {
  return contacts
    .map((contact) => contactToTaskCustomerOption(contact, leads))
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
}

const parseLeadStatus = (value: Lead['status']): LeadStatus | null => {
  if (
    value === 'new' ||
    value === 'inProgress' ||
    value === 'waitingFinance' ||
    value === 'closed' ||
    value === 'lost'
  ) {
    return value;
  }
  return null;
};

const toTimeMs = (value: Lead['updatedAt'] | Lead['createdAt'] | Lead['lastActionDate']): number => {
  if (!value) return -Infinity;
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'string') {
    const t = new Date(value).getTime();
    return Number.isNaN(t) ? -Infinity : t;
  }
  return -Infinity;
};

/** Most recent interaction timestamp for sorting which lead to surface in CRM UI. */
const leadRecencyScore = (lead: Lead): number =>
  Math.max(toTimeMs(lead.updatedAt), toTimeMs(lead.lastActionDate), toTimeMs(lead.createdAt));

/** True when the lead is still in the funnel (not won/lost yet). */
export function isLeadInActivePipeline(lead: Lead): boolean {
  const s = parseLeadStatus(lead.status);
  return s !== null && s !== 'closed' && s !== 'lost';
}

/**
 * Pick which lead drives the preview "About → lead status" chip: prefer active
 * pipeline leads (new / inProgress / waitingFinance), newest activity first;
 * if none are active, fall back to the lead with latest activity overall.
 */
export function pickLeadForAboutStatus(leads: Lead[]): Lead | null {
  if (leads.length === 0) return null;

  const byRecencyThenCreated = (a: Lead, b: Lead): number => {
    const activityDiff = leadRecencyScore(b) - leadRecencyScore(a);
    if (activityDiff !== 0) return activityDiff;
    return toTimeMs(b.createdAt) - toTimeMs(a.createdAt);
  };

  const active = leads.filter(isLeadInActivePipeline);
  const pool = active.length > 0 ? active : leads;
  return [...pool].sort(byRecencyThenCreated)[0] ?? null;
}

/**
 * Stage from all linked leads: **active pipeline beats historical closed**.
 * - `opportunity`: at least one lead in new / inProgress / waitingFinance
 * - `customer`: at least one closed (and none in active pipeline above)
 * - `prospect`: otherwise (e.g. only lost leads)
 *
 * Mirrors `inferStageFromLeads` in `migrateCustomersToContacts.ts`.
 */
export function inferContactStageFromLeads(leads: Lead[]): ContactStage {
  if (
    leads.some((l) => {
      const s = parseLeadStatus(l.status);
      return s !== null && s !== 'closed' && s !== 'lost';
    })
  ) {
    return 'opportunity';
  }
  if (leads.some((l) => parseLeadStatus(l.status) === 'closed')) return 'customer';
  return 'prospect';
}

/**
 * Load linked sales leads. The React Query cache is often incomplete during a
 * save, and `inferContactStageFromLeads([])` is `prospect` — that used to
 * overwrite a manual opportunity with a history entry and no `changedByUserId`.
 */
async function loadLeadsByIds(
  ids: string[],
  cache: Lead[],
  savedLead?: Lead,
): Promise<{ leads: Lead[]; unresolvedIds: string[] }> {
  const byId = new Map<string, Lead>();
  if (savedLead) byId.set(savedLead.id, savedLead);
  for (const cached of cache) {
    if (cached.id) byId.set(cached.id, cached);
  }

  const leads: Lead[] = [];
  const unresolvedIds: string[] = [];
  for (const id of ids) {
    let lead = byId.get(id);
    if (!lead) {
      const snap = await getDoc(doc(db.leads, id));
      lead = snap.data();
      if (lead) byId.set(id, lead);
    }
    if (lead) leads.push(lead);
    else unresolvedIds.push(id);
  }
  return { leads, unresolvedIds };
}

const applyInferredSalesStage = (
  contact: Contact,
  relatedLeads: Lead[],
  unresolvedIds: string[],
  now: Date,
): Pick<Contact, 'stage' | 'stageHistory'> => {
  // Manual `unqualified` is sticky. Incomplete lead data must not infer `prospect`.
  if (contact.stage === 'unqualified' || unresolvedIds.length > 0) {
    return { stage: contact.stage, stageHistory: contact.stageHistory };
  }
  const stage = inferContactStageFromLeads(relatedLeads);
  const stageHistory = appendContactStageHistory(contact.stageHistory, stage, now);
  return {
    stage: getCurrentContactStage(stageHistory, stage),
    stageHistory,
  };
};

/**
 * Ensures contacts match this lead attach `lead.id` and recompute `stage` from all linked leads.
 * When `lead.contactId` is set, only that contact is updated (no full-collection soft match).
 */
export async function syncContactsAfterLeadSaved(
  lead: Lead,
  queryClient: QueryClient,
): Promise<void> {
  const leadsCache = queryClient.getQueryData<Lead[]>(['leads']) ?? [];
  const leadContactId = lead.contactId?.trim();

  const contactsToSync: Contact[] = [];
  if (leadContactId) {
    const snap = await getDoc(doc(db.contacts, leadContactId));
    const contact = snap.data();
    if (contact) contactsToSync.push(contact);
  } else {
    const snapshot = await getDocs(db.contacts);
    for (const d of snapshot.docs) {
      const contact = d.data();
      if (contactMatchesLead(contact, lead)) contactsToSync.push(contact);
    }
  }

  for (const contact of contactsToSync) {
    const mergedLeadIds = [...new Set([...(contact.leadIds ?? []), lead.id])];
    const { leads: relatedLeads, unresolvedIds } = await loadLeadsByIds(
      mergedLeadIds,
      leadsCache,
      lead,
    );

    const now = new Date();
    const { stage, stageHistory } = applyInferredSalesStage(
      contact,
      relatedLeads,
      unresolvedIds,
      now,
    );

    await setDoc(doc(db.contacts, contact.id), {
      ...contact,
      leadIds: mergedLeadIds,
      stage,
      stageHistory,
      updatedAt: now,
    });
  }

  await queryClient.invalidateQueries({ queryKey: CONTACTS_QUERY_KEY });
}

export async function syncContactsAfterLeadDeleted(
  leadId: string,
  queryClient: QueryClient,
): Promise<void> {
  const snapshot = await getDocs(db.contacts);
  const leadsCache = queryClient.getQueryData<Lead[]>(['leads']) ?? [];

  for (const d of snapshot.docs) {
    const contact = d.data();
    if (!(contact.leadIds ?? []).includes(leadId)) continue;

    const mergedLeadIds = (contact.leadIds ?? []).filter((id) => id !== leadId);
    const { leads: relatedLeads, unresolvedIds } = await loadLeadsByIds(
      mergedLeadIds,
      leadsCache,
    );

    const now = new Date();
    const { stage, stageHistory } = applyInferredSalesStage(
      contact,
      relatedLeads,
      unresolvedIds,
      now,
    );

    await setDoc(doc(db.contacts, contact.id), {
      ...contact,
      leadIds: mergedLeadIds,
      stage,
      stageHistory,
      updatedAt: now,
    });
  }

  await queryClient.invalidateQueries({ queryKey: CONTACTS_QUERY_KEY });
}

/** True when an inventory lead is still in the funnel (not won/lost). */
export function isInventoryLeadInActivePipeline(lead: InventoryLead): boolean {
  const status = lead.status;
  return status != null && status !== '' && status !== 'won' && status !== 'lost';
}

/**
 * Inventory stage from linked inventory leads (same rules as sales):
 * - `opportunity`: at least one active lead (not won/lost)
 * - `customer`: at least one `won` and none active
 * - `prospect`: otherwise (e.g. only lost, or no leads)
 */
export function inferContactInventoryStageFromLeads(leads: InventoryLead[]): ContactStage {
  if (leads.some(isInventoryLeadInActivePipeline)) return 'opportunity';
  if (leads.some((l) => l.status === 'won')) return 'customer';
  return 'prospect';
}

const collectInventoryLeadsForContact = (
  contactId: string,
  savedLead: InventoryLead | null,
  excludeLeadId: string | null,
  cache: InventoryLead[],
): InventoryLead[] => {
  const byId = new Map<string, InventoryLead>();
  for (const lead of cache) {
    if (lead.contactId !== contactId) continue;
    if (excludeLeadId && lead.id === excludeLeadId) continue;
    byId.set(lead.id, lead);
  }
  if (savedLead && savedLead.contactId === contactId) {
    byId.set(savedLead.id, savedLead);
  }
  return [...byId.values()];
};

const writeContactInventoryStage = async (
  contact: Contact,
  relatedLeads: InventoryLead[],
): Promise<void> => {
  // Manual `unqualified` is sticky — inventory lead sync does not override it.
  if (contact.inventoryStage === 'unqualified') return;

  const now = new Date();
  const inventoryStage = inferContactInventoryStageFromLeads(relatedLeads);
  const inventoryStageHistory = appendContactStageHistory(
    contact.inventoryStageHistory,
    inventoryStage,
    now,
  );
  const currentInventoryStage = getCurrentContactStage(
    inventoryStageHistory,
    inventoryStage,
  );

  await setDoc(doc(db.contacts, contact.id), {
    ...contact,
    inventoryStage: currentInventoryStage,
    inventoryStageHistory,
    updatedAt: now,
  });
};

/**
 * Recomputes `inventoryStage` for the lead's contact (and optionally a previous
 * contact when `contactId` changed on update).
 */
export async function syncContactInventoryStageAfterLeadSaved(
  lead: InventoryLead,
  queryClient: QueryClient,
  previousContactId?: string | null,
): Promise<void> {
  const inventoryCache =
    queryClient.getQueryData<InventoryLead[]>(INVENTORY_LEADS_QUERY_KEY) ?? [];

  const contactIds = new Set<string>([lead.contactId]);
  if (previousContactId && previousContactId !== lead.contactId) {
    contactIds.add(previousContactId);
  }

  for (const contactId of contactIds) {
    if (!contactId) continue;
    const contactSnap = await getDoc(doc(db.contacts, contactId));
    if (!contactSnap.exists()) continue;
    const contact = contactSnap.data();

    const relatedLeads = collectInventoryLeadsForContact(
      contactId,
      lead,
      null,
      inventoryCache,
    );
    // When syncing the previous contact after a reassignment, exclude this lead.
    const leadsForContact =
      contactId === lead.contactId
        ? relatedLeads
        : collectInventoryLeadsForContact(contactId, null, lead.id, inventoryCache);

    await writeContactInventoryStage(contact, leadsForContact);
  }

  await queryClient.invalidateQueries({ queryKey: CONTACTS_QUERY_KEY });
}

export async function syncContactInventoryStageAfterLeadDeleted(
  deletedLead: Pick<InventoryLead, 'id' | 'contactId'>,
  queryClient: QueryClient,
): Promise<void> {
  if (!deletedLead.contactId) return;

  const contactSnap = await getDoc(doc(db.contacts, deletedLead.contactId));
  if (!contactSnap.exists()) return;
  const contact = contactSnap.data();

  const inventoryCache =
    queryClient.getQueryData<InventoryLead[]>(INVENTORY_LEADS_QUERY_KEY) ?? [];
  const relatedLeads = collectInventoryLeadsForContact(
    deletedLead.contactId,
    null,
    deletedLead.id,
    inventoryCache,
  );

  await writeContactInventoryStage(contact, relatedLeads);
  await queryClient.invalidateQueries({ queryKey: CONTACTS_QUERY_KEY });
}
